import React, { useCallback,useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';

import { useJotCoachRules } from '../store/hooks';

import { ComplianceRuleIndicator } from './ComplianceRuleIndicator';
import { ComplianceRulePopover } from './ComplianceRulePopover';

export const ComplianceRuleIndicatorsRow: React.FC = () => {
  const { rules: complianceRules, isLoading } = useJotCoachRules();
  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const indicatorRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const closeTimeoutRef = useRef<NodeJS.Timeout>();

  const cancelCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  }, []);

  const handleIndicatorEnter = useCallback((ruleId: string) => {
    cancelCloseTimeout();
    setHoveredRuleId(ruleId);
  }, [cancelCloseTimeout]);

  const handleIndicatorLeave = useCallback(() => {
    // Delay closing to allow mouse to move to popover
    // This timeout will be cancelled if mouse enters popover
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRuleId(null);
    }, 150);
  }, [cancelCloseTimeout]);

  const handlePopoverMouseEnter = useCallback(() => {
    // Cancel the close timeout when mouse enters popover
    cancelCloseTimeout();
  }, [cancelCloseTimeout]);

  const handlePopoverMouseLeave = useCallback(() => {
    // Start close timeout when mouse leaves popover
    cancelCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredRuleId(null);
    }, 150);
  }, [cancelCloseTimeout]);

  const handlePopoverClose = useCallback(() => {
    cancelCloseTimeout();
    setHoveredRuleId(null);
  }, [cancelCloseTimeout]);

  if (isLoading || complianceRules.length === 0) {
    return null;
  }

  const hoveredRule = hoveredRuleId
    ? complianceRules.find(rule => rule.id === hoveredRuleId) ?? null
    : null;

  const anchorEl = hoveredRuleId ? indicatorRefs.current[hoveredRuleId] : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center',
        }}
      >
        {complianceRules.map(rule => (
          <Box
            key={rule.id}
            ref={(el: HTMLElement | null) => {
              indicatorRefs.current[rule.id] = el;
            }}
          >
            <ComplianceRuleIndicator
              rule={rule}
              isHovered={hoveredRuleId === rule.id}
              onMouseEnter={() => handleIndicatorEnter(rule.id)}
              onMouseLeave={handleIndicatorLeave}
            />
          </Box>
        ))}
      </Stack>

      <ComplianceRulePopover
        rule={hoveredRule}
        anchorEl={anchorEl}
        open={Boolean(hoveredRule && anchorEl)}
        onClose={handlePopoverClose}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
      />
    </Box>
  );
};

